import { yupResolver } from '@hookform/resolvers/yup';
import AddCommentOutlined from '@mui/icons-material/AddCommentOutlined';
import ArrowBack from '@mui/icons-material/ArrowBack';
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';
import Lock from '@mui/icons-material/Lock';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ModelError_Response } from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import TextInput from '~components/FormFields/v2/TextInput';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { TreadAgentSkill, TreadAgentSkillBasic } from '~hooks/useTreadAgent/models';
import useTreadAgentSkills from '~hooks/useTreadAgentSkills/useTreadAgentSkills';
import useTreadAgentSkillQuery from '~queries/useTreadAgentSkillQuery';
import TreadAgentLoader from '~tread-agent/TreadAgentLoader';
import TreadAgentSkillDeleteDialog from '~tread-agent/TreadAgentSkillDeleteDialog';
import {
  setTreadAgentSkillDefaultValues,
  SKILL_CONTENT_MAX_LENGTH,
  SKILL_DESCRIPTION_MAX_LENGTH,
  TreadAgentSkillDto,
  treadAgentSkillSchema,
} from '~tread-agent/treadAgentSkillSchema';
import TreadAgentSkillsColumn from '~tread-agent/TreadAgentSkillsColumn';

const CONTENT_MIN_ROWS = 14;

function BackToSkillsButton({
  isDisabled,
  onBack,
}: {
  isDisabled?: boolean;
  onBack: () => void;
}) {
  return (
    <Box sx={{ ml: -1, mb: 1 }}>
      <SmallButton
        color="brandV2Black"
        disabled={isDisabled}
        onClick={onBack}
        startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
        sx={{ color: 'text.primary' }}
        variant="text"
      >
        {t('tread_agent.skills.back_to_skills')}
      </SmallButton>
    </Box>
  );
}

function SkillPaneColumn({
  children,
  isBackDisabled,
  onBack,
}: {
  children: ReactNode;
  isBackDisabled?: boolean;
  onBack: () => void;
}) {
  return (
    <TreadAgentSkillsColumn>
      <BackToSkillsButton isDisabled={isBackDisabled} onBack={onBack} />

      {children}
    </TreadAgentSkillsColumn>
  );
}

interface TreadAgentSkillPaneProps {
  canCreate: boolean;
  canDelete: boolean;
  canEdit: boolean;
  companyId: string;
  onBack: () => void;
  onUse: (skillName: string) => void;
  skill: TreadAgentSkillBasic | null;
}

interface SkillFormProps extends Omit<TreadAgentSkillPaneProps, 'skill'> {
  detail?: TreadAgentSkill;
}

function SkillForm({
  canCreate,
  canDelete,
  canEdit,
  companyId,
  detail,
  onBack,
  onUse,
}: SkillFormProps) {
  const { createSkill, updateSkill } = useTreadAgentSkills();
  const [isSaving, setIsSaving] = useState(false);
  const isMountedRef = useRef(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
  } = useForm<TreadAgentSkillDto>({
    resolver: yupResolver(treadAgentSkillSchema),
    defaultValues: setTreadAgentSkillDefaultValues(detail),
  });
  const watchedDescription = useWatch({ control, name: 'description' });
  const watchedContent = useWatch({ control, name: 'content' });

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const isCreating = !detail;
  const isReadOnly = isCreating ? !canCreate : !canEdit;

  const applyServerErrors = (error: unknown) => {
    if (!isAxiosError<ModelError_Response>(error) || error.response?.status !== 422) {
      return;
    }

    const unmatchedMessages: string[] = [];

    for (const { field, message } of error.response.data.error.errors ?? []) {
      if (field === 'name' || field === 'description' || field === 'content') {
        setError(field, { message });
      } else {
        unmatchedMessages.push(message);
      }
    }

    if (unmatchedMessages.length > 0) {
      setError('root', { message: unmatchedMessages.join(' ') });
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    setIsSaving(true);

    try {
      if (detail?.id) {
        await updateSkill(companyId, detail.id, values);
      } else {
        await createSkill(companyId, values);
      }
      if (isMountedRef.current) {
        onBack();
      }
    } catch (error) {
      applyServerErrors(error);
    } finally {
      setIsSaving(false);
    }
  });

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}
    >
      <SkillPaneColumn isBackDisabled={isSaving} onBack={onBack}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography sx={{ fontWeight: 600 }} variant="h6">
            {isCreating ? t('tread_agent.skills.new_skill') : detail.name}
          </Typography>

          <TextInput
            control={control}
            isDisabled={isReadOnly || isSaving}
            slotProps={{ htmlInput: { 'aria-label': t('tread_agent.skills.name') } }}
            isRequired
            label={t('tread_agent.skills.name')}
            name="name"
            placeholder={t('tread_agent.skills.name_placeholder')}
          />

          <TextInput
            control={control}
            isDisabled={isReadOnly || isSaving}
            slotProps={{
              htmlInput: { 'aria-label': t('tread_agent.skills.description') },
            }}
            isRequired
            label={t('tread_agent.skills.description')}
            minRows={2}
            name="description"
          />

          <Typography color="text.secondary" sx={{ mt: -1.5 }} variant="caption">
            {`${watchedDescription.length}/${SKILL_DESCRIPTION_MAX_LENGTH}`}
          </Typography>

          <TextInput
            control={control}
            isDisabled={isReadOnly || isSaving}
            slotProps={{
              htmlInput: { 'aria-label': t('tread_agent.skills.instructions') },
            }}
            isRequired
            label={t('tread_agent.skills.instructions')}
            minRows={CONTENT_MIN_ROWS}
            name="content"
          />

          <Typography color="text.secondary" sx={{ mt: -1.5 }} variant="caption">
            {`${watchedContent.length}/${SKILL_CONTENT_MAX_LENGTH}`}
          </Typography>

          {errors.root?.message && (
            <Typography color="error" variant="caption">
              {errors.root.message}
            </Typography>
          )}

          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 1,
              mt: 1,
            }}
          >
            {!isCreating && canDelete && (
              <SmallButton
                color="error"
                disabled={isSaving}
                onClick={() => setIsDeleteDialogOpen(true)}
                startIcon={<DeleteOutlineOutlined sx={{ fontSize: 16 }} />}
                variant="outlined"
              >
                {t('actions.delete')}
              </SmallButton>
            )}

            <Box sx={{ flexGrow: 1 }} />

            {!isCreating && (
              <SmallButton
                color="brandV2Black"
                disabled={isSaving}
                onClick={() => onUse(detail.name)}
                startIcon={<AddCommentOutlined sx={{ fontSize: 16 }} />}
                sx={{ borderColor: 'divider', color: 'text.primary' }}
                variant="outlined"
              >
                {t('tread_agent.skills.use')}
              </SmallButton>
            )}

            <SmallButton
              color="brandV2Black"
              disabled={isSaving}
              onClick={onBack}
              sx={{ borderColor: 'divider', color: 'text.primary' }}
              variant="outlined"
            >
              {t('actions.cancel')}
            </SmallButton>

            {!isReadOnly && (
              <SmallLoadingButton color="brandV2Black" loading={isSaving} type="submit">
                {t('actions.save')}
              </SmallLoadingButton>
            )}
          </Box>
        </Box>
      </SkillPaneColumn>

      <TreadAgentSkillDeleteDialog
        companyId={companyId}
        onClose={() => setIsDeleteDialogOpen(false)}
        onDeleted={onBack}
        skill={isDeleteDialogOpen ? (detail ?? null) : null}
      />
    </Box>
  );
}

export default function TreadAgentSkillPane({
  skill,
  ...formProps
}: TreadAgentSkillPaneProps) {
  const detailQuery = useTreadAgentSkillQuery(
    formProps.companyId,
    skill?.isSystem ? null : skill?.id,
  );

  if (skill?.isSystem) {
    return (
      <SkillPaneColumn onBack={formProps.onBack}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            <Typography sx={{ fontWeight: 600 }} variant="h6">
              {skill.name}
            </Typography>

            <Lock color="action" fontSize="small" />
          </Box>

          <Typography>{skill.description}</Typography>

          <Typography color="text.secondary" variant="body2">
            {t('tread_agent.skills.built_in_message')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <SmallButton
              color="brandV2Black"
              onClick={() => formProps.onUse(skill.name)}
              startIcon={<AddCommentOutlined sx={{ fontSize: 16 }} />}
            >
              {t('tread_agent.skills.use')}
            </SmallButton>
          </Box>
        </Box>
      </SkillPaneColumn>
    );
  }

  if (skill && !detailQuery.data) {
    return (
      <SkillPaneColumn onBack={formProps.onBack}>
        {detailQuery.isError ? (
          <Typography color="text.secondary">
            {t('error_messages.tread_agent.failed_to_fetch_skill')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <TreadAgentLoader size={24} />
          </Box>
        )}
      </SkillPaneColumn>
    );
  }

  return <SkillForm key={skill?.id ?? 'new'} detail={detailQuery.data} {...formProps} />;
}
